import { useMemo, useState } from "react";

import { useMethodology, useRunValidation, useValidation } from "@/api/queries";
import type { Json } from "@/api/types";
import { Button } from "@/components/Button";
import { DataTable } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { RemoteState } from "@/components/RemoteState";
import { RuleStatus, StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import { useUiStore } from "@/store/ui";

/** @dataSource remote — 规则目录来自方法论包，问题明细来自确定性校验器 */

interface CheckRule {
  id: string;
  name: string;
  detail: string;
  status: "pass" | "warning" | "danger";
  count: number;
  layer: string;
  owner: string;
}

const LAYER_OWNER: Record<string, string> = {
  INVARIANT: "系统",
  HEURISTIC: "总编剧",
  EXPERIMENT: "制片",
};

function asString(value: Json | undefined, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

/** 结构检查：规则目录 + 问题明细，均来自后端（R01-R22 / V101-V108）。 */
export function ChecksView() {
  const projectId = useUiStore((state) => state.projectId);
  const validation = useValidation(projectId);
  const methodology = useMethodology();
  const runValidation = useRunValidation(projectId);
  const pushToast = useUiStore((state) => state.pushToast);
  const [selectedRule, setSelectedRule] = useState<CheckRule | null>(null);

  const issues = validation.data?.issues ?? [];
  const summary = validation.data?.summary ?? {};

  const rules = useMemo<CheckRule[]>(() => {
    const catalog = methodology.data?.rules ?? [];
    return catalog
      .filter(
        (item): item is Record<string, Json> =>
          typeof item === "object" && item !== null && !Array.isArray(item),
      )
      .map((item) => {
        const validator = asString(item.validator);
        const layer = asString(item.layer, "HEURISTIC");
        const matched = issues.filter((issue) => issue.rule_id === validator);
        const hasError = matched.some((issue) => issue.severity === "error");
        return {
          id: asString(item.id),
          name: validator || asString(item.id),
          detail: asString(item.statement),
          status: hasError ? "danger" : matched.length ? "warning" : "pass",
          count: matched.length,
          layer,
          owner: LAYER_OWNER[layer] ?? "总编剧",
        };
      });
  }, [methodology.data, issues]);

  const totalIssues = Number(summary.total ?? issues.length);
  const blocking = Number(summary.blocking ?? 0);
  const passed = Boolean(summary.passed ?? blocking === 0);
  const score = Number(summary.score ?? 0);

  const ruleIssues = selectedRule
    ? issues.filter((issue) => issue.rule_id === selectedRule.name)
    : [];

  return (
    <RemoteState queries={[validation, methodology]} title="检查数据加载失败">
      <div className="page-scroll">
        <div className="page-width">
          <header className="page-heading">
            <div>
              <span className="eyebrow">确定性规则 · {rules.length} 条</span>
              <h2>{blocking > 0 ? "存在阻塞问题" : "无阻塞问题"}</h2>
            </div>
            <Button
              variant="primary"
              id="run-check-page"
              disabled={runValidation.isPending || !projectId}
              onClick={() =>
                runValidation.mutate(undefined, {
                  onSuccess: (result) => {
                    const count = Number(result.summary.blocking ?? 0);
                    pushToast({
                      title: count > 0 ? `${count} 项阻塞` : "检查通过",
                      detail: count > 0 ? "阻塞项须先修复，下游产物不得标记正式" : "可提交关口审核",
                      mark: count > 0 ? "!" : "✓",
                    });
                  },
                  onError: (error) =>
                    pushToast({
                      title: "检查失败",
                      detail: error instanceof Error ? error.message : "未知错误",
                      mark: "!",
                    }),
                })
              }
            >
              {runValidation.isPending ? "检查中…" : "重新运行检查"}
            </Button>
          </header>

          <section className="check-hero">
            <div className="check-score">
              <div className="check-score-number">
                {score}
                <small>结构健康度 / 100</small>
              </div>
              <div>
                <h2>
                  {runValidation.isPending
                    ? "正在重跑确定性规则"
                    : passed
                      ? "全部规则通过"
                      : `发现 ${totalIssues} 项问题`}
                </h2>
                <p>
                  方法论 {validation.data?.methodology_version ?? "—"} · Profile{" "}
                  {validation.data?.profile_version ?? "—"}
                </p>
              </div>
            </div>
            <div className="check-stat-grid">
              <div className="check-stat">
                <strong>{totalIssues}</strong>
                <span>问题总数</span>
              </div>
              <div className="check-stat">
                <strong>{blocking}</strong>
                <span>阻塞项</span>
              </div>
              <div className="check-stat">
                <strong>{rules.filter((rule) => rule.status === "pass").length}</strong>
                <span>无问题规则</span>
              </div>
              <div className="check-stat">
                <strong>{rules.length}</strong>
                <span>规则总数</span>
              </div>
            </div>
          </section>

          <section>
            <div className="section-title">
              <div>
                <h3>检查规则</h3>
                <p>点击可查看该规则下的问题明细</p>
              </div>
              <Tag>方法包 {methodology.data?.version ?? "—"}</Tag>
            </div>

            <DataTable<CheckRule>
              rows={rules}
              rowKey={(rule) => rule.id}
              columns={[
                {
                  key: "name",
                  header: "规则",
                  className: "rule-name",
                  render: (rule) => (
                    <>
                      <strong>
                        {rule.id} · {rule.name}
                      </strong>
                      <small>{rule.detail}</small>
                    </>
                  ),
                },
                {
                  key: "status",
                  header: "状态",
                  render: (rule) => <RuleStatus status={rule.status} />,
                },
                { key: "count", header: "问题", render: (rule) => <strong>{rule.count}</strong> },
                {
                  key: "owner",
                  header: "层级",
                  render: (rule) => <Tag>{rule.layer}</Tag>,
                },
                {
                  key: "action",
                  header: "动作",
                  render: (rule) => (
                    <Button size="small" variant="quiet" onClick={() => setSelectedRule(rule)}>
                      查看
                    </Button>
                  ),
                },
              ]}
            />
          </section>

          <section style={{ marginTop: 24 }}>
            <div className="section-title">
              <div>
                <h3>问题明细</h3>
                <p>来自最近一次确定性校验</p>
              </div>
              <StatusLabel tone={blocking > 0 ? "danger" : "success"}>
                {blocking > 0 ? `${blocking} 项阻塞` : "无阻塞"}
              </StatusLabel>
            </div>
            {issues.length === 0 ? (
              <div className="panel">
                <div className="panel-body">
                  <div className="empty-state">
                    <strong>暂无问题明细</strong>
                    <p>运行一次结构检查后，这里会列出每条问题的定位与建议。</p>
                  </div>
                </div>
              </div>
            ) : (
              <ul className="issue-list">
                {issues.map((issue, index) => (
                  <li className="issue-item" key={`${issue.rule_id}-${index}`}>
                    <span className="mini-mark">{issue.severity === "error" ? "!" : "?"}</span>
                    <div>
                      <p>
                        {issue.rule_id} · {issue.message}
                      </p>
                      <small>
                        {issue.field_path || "—"}
                        {issue.suggestion ? ` · 建议：${issue.suggestion}` : ""}
                      </small>
                    </div>
                    <span aria-hidden="true">›</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <Modal
          open={Boolean(selectedRule)}
          onClose={() => setSelectedRule(null)}
          eyebrow={selectedRule ? `规则 ${selectedRule.id}` : ""}
          title={selectedRule ? selectedRule.name : ""}
          footer={
            <Button variant="quiet" onClick={() => setSelectedRule(null)}>
              关闭
            </Button>
          }
        >
          <p className="inline-note">{selectedRule?.detail}</p>
          {ruleIssues.length === 0 ? (
            <p className="inline-note" style={{ marginTop: 12 }}>
              该规则最近一次运行未发现问题。
            </p>
          ) : (
            <ul className="issue-list" style={{ marginTop: 12 }}>
              {ruleIssues.map((issue, index) => (
                <li className="issue-item" key={index}>
                  <span className="mini-mark">{issue.severity === "error" ? "!" : "?"}</span>
                  <div>
                    <p>{issue.message}</p>
                    <small>{issue.field_path}</small>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      </div>
    </RemoteState>
  );
}
